import { useState, useRef, useEffect } from "react";
import { Mic, Square, Loader2, Volume2, MessageSquare, RotateCcw, ArrowRight, Star, Trash2, BookMarked } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function InterviewPractice({ context = "", suggested = "", result = null }) {
  const [question, setQuestion] = useState(suggested);
  const [phase, setPhase] = useState("idle"); // idle | recording | transcribing | scoring | done
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [saved, setSaved] = useState([]);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => { setQuestion(suggested); }, [suggested]);
  useEffect(() => {
    api.get("/career/questions").then(({ data }) => setSaved(data.questions || [])).catch(() => {});
  }, []);
  useEffect(() => () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    audioRef.current?.pause();
  }, []);

  const suggestions = (result?.blocks || [])
    .flatMap((b) => b.points || [])
    .map((p) => p.split(/\s+[—–]\s+/)[0].trim())
    .filter((q) => q.length > 8)
    .slice(0, 6);

  const saveText = async (q) => {
    const text = (q || "").trim();
    if (text.length < 5) return toast.error("Question is too short to save");
    try {
      const { data } = await api.post("/career/questions", { question: text, context });
      if (!data.duplicate) setSaved((s) => [{ id: data.id, question: data.question, context: data.context }, ...s]);
      toast.success(data.duplicate ? "Already in your bank" : "Saved to your question bank");
    } catch { toast.error("Couldn't save"); }
  };

  const delSaved = async (id) => {
    try { await api.delete(`/career/questions/${id}`); setSaved((s) => s.filter((x) => x.id !== id)); }
    catch { toast.error("Couldn't delete"); }
  };

  const start = async () => {
    if (!question.trim()) return toast.error("Pick or type a question first");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;
      mediaRef.current = mr;
      mr.start();
      setPhase("recording"); setSeconds(0); setFeedback(null); setTranscript("");
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch { toast.error("Microphone access is needed to practise."); }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
  };

  const handleStop = async () => {
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (blob.size < 800) { setPhase("idle"); return toast.error("That was too short — try again."); }
    setPhase("transcribing");
    try {
      const form = new FormData();
      form.append("file", blob, "answer.webm");
      const { data } = await api.post("/voice/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } });
      setTranscript(data.transcript);
      setPhase("scoring");
      const { data: fb } = await api.post("/career/interview-feedback", { question, answer: data.transcript, context });
      setFeedback(fb.result);
      setPhase("done");
      speak(fb.result.summary);
    } catch (e) {
      setPhase("idle");
      toast.error(e?.response?.data?.detail || "Couldn't process that answer. Please try again.");
    }
  };

  const speak = async (text) => {
    if (!text) return;
    try {
      setSpeaking(true);
      const res = await api.post("/voice/tts", { text: text.slice(0, 3800), voice: "nova" }, { responseType: "blob" });
      audioRef.current?.pause();
      const audio = new Audio(URL.createObjectURL(res.data));
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch { setSpeaking(false); }
  };

  const busy = phase === "transcribing" || phase === "scoring";

  return (
    <div className="rounded-lg border border-brand/30 bg-brand-soft p-4 mt-2" data-testid="interview-practice">
      <div className="flex items-center gap-2 text-brand font-semibold text-sm mb-3">
        <MessageSquare className="w-4 h-4" /> Practise out loud
      </div>
      <textarea
        data-testid="interview-question-input"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        rows={2}
        placeholder="Paste or type a question to practise answering…"
        className="w-full bg-canvas border border-line-strong rounded-lg px-3 py-2 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand transition-colors resize-y"
      />

      {suggestions.length > 0 && (
        <div className="mt-2" data-testid="interview-suggestions">
          <div className="text-xs font-mono2 uppercase tracking-widest text-faint mb-1.5">Suggested questions</div>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((q, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full border border-line bg-canvas pl-3 pr-1 py-1 text-xs text-body">
                <button data-testid={`suggestion-use-${i}`} onClick={() => setQuestion(q)} className="hover:text-brand transition-colors max-w-[240px] truncate">{q}</button>
                <button data-testid={`suggestion-save-${i}`} onClick={() => saveText(q)} className="p-1 rounded-full text-faint hover:text-warning transition-colors" title="Save to bank"><Star className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mt-3 flex-wrap">
        {phase === "recording" ? (
          <button data-testid="interview-stop-button" onClick={stop} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-danger text-onbrand text-sm font-semibold hover:bg-danger/90 transition-colors">
            <Square className="w-4 h-4 fill-current" /> Stop ({String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")})
          </button>
        ) : busy ? (
          <span className="flex items-center gap-2 text-sm text-body"><Loader2 className="w-4 h-4 animate-spin" /> {phase === "transcribing" ? "Transcribing your answer…" : "Scoring your answer…"}</span>
        ) : (
          <button data-testid="interview-record-button" onClick={start} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors">
            <Mic className="w-4 h-4" /> {feedback ? "Answer again" : "Record answer"}
          </button>
        )}
        <button data-testid="interview-save-question" onClick={() => saveText(question)} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-line text-ink text-sm hover:bg-subtle transition-colors">
          <Star className="w-3.5 h-3.5" /> Save question
        </button>
        {feedback && (
          <button data-testid="interview-replay-feedback" onClick={() => speak(feedback.summary)} disabled={speaking} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-line text-ink text-sm hover:bg-subtle transition-colors disabled:opacity-60">
            {speaking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />} Replay
          </button>
        )}
      </div>

      {transcript && (
        <div className="mt-3 text-sm text-body bg-canvas border border-line rounded-lg p-3" data-testid="interview-transcript">
          <span className="text-xs font-mono2 uppercase tracking-widest text-faint">Your answer</span>
          <p className="mt-1 leading-relaxed">{transcript}</p>
        </div>
      )}

      {feedback && (
        <div className="mt-3 space-y-3" data-testid="interview-feedback">
          <p className="text-sm text-ink leading-relaxed border-l-2 border-brand/40 pl-3">{feedback.summary}</p>
          {feedback.blocks.map((b, i) => (
            <div key={i} className="rounded-lg border border-line bg-canvas p-3">
              {b.title && <div className="font-semibold text-sm mb-1.5">{b.title}</div>}
              <ul className="space-y-1">
                {b.points.map((p, j) => (
                  <li key={j} className="flex gap-2 text-sm text-body"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand shrink-0" />{p}</li>
                ))}
              </ul>
            </div>
          ))}
          {feedback.next_steps?.length > 0 && (
            <ul className="space-y-1">
              {feedback.next_steps.map((s, i) => (
                <li key={i} className="flex gap-2 text-sm text-ink"><ArrowRight className="w-4 h-4 text-brand shrink-0 mt-0.5" />{s}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {saved.length > 0 && (
        <div className="mt-4 pt-4 border-t border-line" data-testid="question-bank">
          <div className="flex items-center gap-2 text-sm font-semibold mb-2">
            <BookMarked className="w-4 h-4 text-warning" /> Your question bank ({saved.length})
          </div>
          <div className="space-y-1.5">
            {saved.map((q) => (
              <div key={q.id} data-testid={`saved-question-${q.id}`} className="flex items-center gap-2 rounded-lg border border-line bg-canvas px-3 py-2">
                <span className="flex-1 text-sm text-body truncate">{q.question}</span>
                <button data-testid={`practise-saved-${q.id}`} onClick={() => setQuestion(q.question)} className="text-xs text-brand hover:text-brand transition-colors shrink-0">Practise</button>
                <button data-testid={`delete-saved-${q.id}`} onClick={() => delSaved(q.id)} className="p-1 rounded text-faint hover:text-danger transition-colors shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
